import { ReactNode, useCallback, useMemo, useState } from 'react'

import PlainModal, {
  PlainModalHeader,
  HandleModalDisplayed,
  PlainModalContent,
  PlainModalFooter,
} from 'app/common/ui-next/plain-modal'
import ControlsBar from 'app/common/ui-next/controls-bar'
import PlainButton from 'app/common/ui-next/plain-button'
import { useSaleTypesAsArray, useSaleTypesData } from 'app/dataProviders/SaleTypesData'
import DishesData from 'app/dataProviders/dishes/DishesData'
import usePopup from 'app/common/hooks/usePopup'

import { DeletionPossibilityCheck, OccupiedSaleTypes } from './DeletionPossibilityCheck'
import { DeletionWarning } from './DeletionWarning'
import { OccupiedReplaceForm, prefix } from './OccupiedReplaceForm'
import styles from './DeleteProductionTypeModal.scss'


type Props = {
  saleTypeId: string | Array<string>
  children: (toggle: HandleModalDisplayed) => ReactNode
}

export const DeleteProductionTypeModal = ({ children, saleTypeId }: Props) => {
  const [submitting, setSubmitting] = useState(false)
  const [deletionAllowed, setDeletionAllowed] = useState(false)
  const [occupiedSaleTypes, setOccupiedSaleTypes] = useState<OccupiedSaleTypes>([])

  const saleTypesIdsToBeRemoved = useMemo(() => (
    Array.isArray(saleTypeId) ? saleTypeId : [saleTypeId]
  ), [saleTypeId])

  const {
    saleTypes: saleTypesToBeRemoved,
  } = useSaleTypesAsArray({ sort: 'byTitle', saleTypesIds: saleTypesIdsToBeRemoved })
  const { replaceSaleTypes } = useSaleTypesData()

  const popup = usePopup()

  const handleSubmit = useCallback(async (form: HTMLFormElement) => {
    setSubmitting(true)
    const formData = new FormData(form)
    const params = saleTypesIdsToBeRemoved.map(id => ({
      id,
      replaceId: formData.get(`${prefix}${id}`) as string | null,
    }))

    const res = await replaceSaleTypes(params)
    setSubmitting(false)
    if (res) popup(saleTypesIdsToBeRemoved.length > 1 ? 'Типы продукции успешно удалены' : 'Тип продукции успешно удален')
    return !!res
  }, [saleTypesIdsToBeRemoved, replaceSaleTypes, popup])

  const handleChangeModalDisplayed = useCallback((displayed: boolean) => {
    if (!displayed) {
      setDeletionAllowed(false)
      setOccupiedSaleTypes([])
    }
  }, [])

  return (
    <PlainModal
      formId="deleteProductionType"
      className={styles.modal}
      controlBy={children}
      onSubmit={handleSubmit}
      onDisplayStatusChange={handleChangeModalDisplayed}
    >
      <PlainModalHeader>{'Удаление типа продукции'}</PlainModalHeader>
      <PlainModalContent>
        {/* проверка выбранных типов продукции на их участие в блюдах */}
        {!deletionAllowed
          && <DishesData saleTypeId={saleTypesIdsToBeRemoved} limit={10000}>
            <DeletionPossibilityCheck
              saleTypesToBeRemoved={saleTypesToBeRemoved}
              onOccupiedSaleTypes={setOccupiedSaleTypes}
              onDeletionAllowed={setDeletionAllowed}
            />
          </DishesData>}
        {/* если есть выбранные типы продукции занятые в блюдах */}
        {occupiedSaleTypes.length > 0
          && <OccupiedReplaceForm
            occupiedSaleTypes={occupiedSaleTypes}
            onDeletionAllowed={setDeletionAllowed}
          />}
        {/* если все выбранные типы продукции не заняты в блюдах */}
        {deletionAllowed && occupiedSaleTypes.length === 0
          && <DeletionWarning saleTypesToBeRemoved={saleTypesToBeRemoved} />}
      </PlainModalContent>
      <PlainModalFooter>
        {handleModalDisplayed => (
          <ControlsBar middle>
            <PlainButton
              type="submit"
              disabled={submitting || !deletionAllowed}
              danger
            >
              {submitting ? 'Удаление...' : 'Удалить'}
            </PlainButton>

            <PlainButton
              outline
              onClick={handleModalDisplayed}
            >
              {'Отмена'}
            </PlainButton>
          </ControlsBar>
        )}
      </PlainModalFooter>
    </PlainModal>
  )
}
